import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../../../components/navbar';
import Footer from '../../../../components/footer';
import SearchBar from '../../../../components/SearchBar';
import FormInput from '../../../../components/FormInput';
import FilterButtons from '../../../../components/universal-filter-tabs';
import Pagination from '../../../../components/Pagination';
import styles from '../css/ChronicMedication.module.css';
import ChronicCards from '../components/ChronicCards';
import PatientChronicCards from '../components/PatientChronicCards';

import { fetchPendingChronicCards } from '../../../../api/dispensary/script/chronicApi';
import { fetchScriptDetails } from '../../../../api/dispensary/script/processScriptApi';
import { useAuth } from '../../../../context/AuthContext';

const ChronicMedication = () => {
    const [selectedDate, setSelectedDate] = useState('');
    const [dateEnabled, setDateEnabled] = useState(false);

    const [searchTerm, setSearchTerm] = useState('');
    const [selectedFilter, setSelectedFilter] = useState('All');
    const [currentPage, setCurrentPage] = useState(1);
    const [originalPatients, setOriginalPatients] = useState([]);
    const [filteredPatients, setFilteredPatients] = useState([]);

    const navigate = useNavigate();
    const { user } = useAuth();

    const stats = [
        { title: 'Total Due Today', value: '40 medications scheduled' },
        { title: 'Packaged', value: '10 ready for collection' },
        { title: 'Overdue', value: '4 require attention' },
        { title: 'Upcoming', value: '17 confirm packaging' },
    ];

    const summaryItems = useMemo(() => {
        // Parse the provided stats strings into metric + subtitle and theme
        const themeByTitle = {
            'Total Due Today': 'neutral',
            'Packaged': 'green',
            'Overdue': 'red',
            'Upcoming': 'purple',
        };
        return stats.map((s, idx) => {
            const [metric, ...rest] = s.value.split(' ');
            return {
                id: idx,
                title: s.title,
                metric,
                subtitle: rest.join(' '),
                theme: themeByTitle[s.title] || 'neutral',
            };
        });
    }, [stats]);

    const PAGE_SIZE = 3;
    const totalPages = Math.ceil(filteredPatients.length / PAGE_SIZE);

    // Format a Date as yyyy-MM-dd to match native date input value
    const toYMD = (d) => {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const da = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${da}`;
    };

    const handleDateChange = (e) => {
        const value = e.target.value || '';
        setSelectedDate(value);
        setDateEnabled(!!value);
        setCurrentPage(1);
        const formattedDate = value || null;
        filterPatients(formattedDate, searchTerm, selectedFilter, user?.branchcode);
    };

    const handleSearchChange = (value) => {
        console.log('Value received:', value);
        const term = value || '';
        setSearchTerm(term);
        setCurrentPage(1);
        filterPatients(null, term, selectedFilter, user?.branchcode);
    };

    const handleFilterChange = (filter) => {
        setSelectedFilter(filter);
        setCurrentPage(1);
        const formattedDate = (dateEnabled && selectedDate) ? selectedDate : null;
        filterPatients(formattedDate, searchTerm, filter, user?.branchcode);
    };

    const filterPatients = (date, term, filter, branchcode) => {
        console.log('Filtering with date:', date, 'term:', term, 'filter:', filter, 'branchcode:', branchcode);
        let filtered = originalPatients;

        if (date) {
            filtered = filtered.filter(patient => {
                const patientDate = toYMD(patient.dueDate);
                return patientDate === date;
            });
        }

        if (term) {
            filtered = filtered.filter(patient =>
                patient.name.toLowerCase().includes(term.toLowerCase())
            );
        }

        if (filter !== 'All') {
            filtered = filtered.filter(patient => patient.status === filter);
        }

        if (branchcode) {
            filtered = filtered.filter(patient => patient.branchcode === branchcode);
        }

        console.log('Filtered patients:', filtered);
        setFilteredPatients(filtered);
    };

    useEffect(() => {
        const load = async () => {
            try {
                const items = await fetchPendingChronicCards({ branchcode: user?.branchcode || undefined });
                // For chronic view we only want repeat-based cards (actual repeats),
                // not initial prescriptions that already have a pending payment row.
                const source = Array.isArray(items)
                    ? items.filter(it => it.repeatId != null)
                    : [];

                const mapped = await Promise.all(
                    source.map(async (it) => {
                        const base = {
                            id: it.prescriptionId,
                            name: it.name,
                            phone: it.phone,
                            dueDate: it.preferredDate ? new Date(it.preferredDate) : new Date(),
                            quantity: 0,
                            status: it.status,
                            scriptNo: it.scriptNo,
                            paymentType: it.paymentType || 'Cash',
                            branchcode: it.branchcode,
                            repeatId: it.repeatId || null,
                        };

                        try {
                            if (it?.prescriptionId != null) {
                                const details = await fetchScriptDetails(it.prescriptionId);
                                const drugs = Array.isArray(details?.drugs) ? details.drugs : [];
                                const totalQ = drugs.reduce((sum, d) => sum + (parseInt(d.quantity, 10) || 0), 0);
                                return { ...base, quantity: totalQ };
                            }
                        } catch (e) {
                            console.warn('Failed to fetch script details for', it?.prescriptionId, e);
                        }
                        return base;
                    })
                );
                setOriginalPatients(mapped);
                // On initial load, default filter is 'All' — show everything (optionally branch-scoped)
                const bc = user?.branchcode;
                const initial = bc ? mapped.filter(p => p.branchcode === bc) : mapped;
                setFilteredPatients(initial);
            } catch (e) {
                console.error('Failed to load chronic pending cards', e);
                setOriginalPatients([]);
                setFilteredPatients([]);
            }
        };
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.branchcode]);

    const handleMarkCollected = (patientId) => {
        const p = filteredPatients.find(p => p.id === patientId) || originalPatients.find(p => p.id === patientId);
        navigate(`/customer/dispensary/chronic-medication-quick-view/${patientId}`, {
            state: { paymentType: p?.paymentType || 'Cash', repeatId: p?.repeatId || null }
        });
    };

    const handleReturnToInventory = (patientId) => {
        const p = filteredPatients.find(p => p.id === patientId) || originalPatients.find(p => p.id === patientId);
        navigate(`/customer/dispensary/chronic-medication-quick-view/${patientId}`, {
            state: { mode: 'returnInventory', paymentType: p?.paymentType || 'Cash', repeatId: p?.repeatId || null }
        });
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const paginatedPatients = filteredPatients.slice(
        (currentPage - 1) * PAGE_SIZE,
        currentPage * PAGE_SIZE
    );

    return (
        <div className={styles.container}>
            <Navbar backTo="/customer/dispensary" />
            <main className={styles.main}>
                <h1 className={styles.title}>Chronic Medication</h1>
                <ChronicCards items={summaryItems} />
                <div className={styles.searchContainer}>
                    <SearchBar
                        value={searchTerm}
                        onChange={handleSearchChange}
                        placeholder="Search Patient"
                    />
                </div>
                <div className={styles.filterButtons}>
                    <FilterButtons
                        buttons={['All', 'Due', 'Overdue']}
                        activeButton={selectedFilter}
                        onButtonClick={handleFilterChange}
                    />
                    <div className={styles.datePickerContainer}>
                        <FormInput
                            type="date"
                            name="filterDate"
                            value={selectedDate}
                            onChange={handleDateChange}
                        />
                    </div>
                </div>
                {totalPages > 1 && (
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                    />
                )}
                <div className={styles.paddingStyle}></div>
                <PatientChronicCards
                    patients={paginatedPatients}
                    onMarkCollected={handleMarkCollected}
                    onReturnToInventory={handleReturnToInventory}
                    showScript={false}
                />
            </main>
            <Footer />
        </div>
    );
};

export default ChronicMedication;